import React, { useMemo, useState } from "react";
import { Card, Row, Col, Typography, Steps, Space, Image, Divider } from "antd";
import { AppstoreAddOutlined, ArrowUpOutlined, ArrowDownOutlined, ReloadOutlined } from "@ant-design/icons";
import CustomCard from "../extraShit/CustomCard";
import { useTranslation } from "react-i18next";
import GamePage from "./GamePage";
import { buildStepItems } from "./stepItems";
import "./greekTemple.css";

const { Paragraph, Text } = Typography;

const baseStepImages = [
    { key: "pillar1", src: "ace" },
    { key: "pillar2", src: "5" },
    { key: "pillar3", src: "3" },
];

const sampleTurns = [
    { id: 1, base: "ace", newCard: "king" },
    { id: 2, base: "5", newCard: "8" },
    { id: 3, base: "3", newCard: "10" },
];

const stackedPillars = [
    { base: "ace", mid: "king", top: "2" },
    { base: "5", mid: "8", top: "queen" },
    { base: "3", mid: "10", top: "7" },
];

const resetSamples = [
    { base: "ace", mid: "5", top: "jack" },
    { base: "3", mid: "queen", top: "9" },
    { base: "4", mid: "7", top: "2" },
];

type PillarStackItem = { base: string; mid: string; top: string };

const PillarStack: React.FC<{ items: PillarStackItem[] }> = ({ items }) => {
    const { t } = useTranslation();
    return (
        <div className="greek-temple-pillar-stack">
            {items.map((pillar, index) => (
                <div key={`${pillar.base}-${pillar.mid}-${pillar.top}`} className="greek-temple-pillar">
                    {[pillar.top, pillar.mid, pillar.base].map((card, cardIndex) => (
                        <Image
                            key={`${card}-${cardIndex}`}
                            src={`/cards/${card}.png`}
                            width={46}
                            height={64}
                            alt={`${["Top", "Middle", "Base"][cardIndex]} card`}
                            preview={false}
                            className="greek-temple-card-image"
                        />
                    ))}
                    <Text type="secondary">{t("games.details.greekTemple.turns.pillar", { id: index + 1 })}</Text>
                </div>
            ))}
        </div>
    );
};

const GrichischerTempel: React.FC = () => {
    const [current, setCurrent] = useState(0);
    const { t } = useTranslation();

    const steps = useMemo(
        () => [
            {
                title: t("games.details.greekTemple.steps.setup.title"),
                description: t("games.details.greekTemple.steps.setup.description"),
                content: (
                    <CustomCard>
                        <Paragraph>
                            <Text strong>{t("games.details.greekTemple.setup.heading")}</Text> {t("games.details.greekTemple.setup.line1")}
                        </Paragraph>
                        <Paragraph>{t("games.details.greekTemple.setup.line2")}</Paragraph>
                        <div className="greek-temple-base-cards">
                            {baseStepImages.map((pillar) => (
                                <Image
                                    key={pillar.key}
                                    src={`/cards/${pillar.src}.png`}
                                    width={64}
                                    height={88}
                                    alt={pillar.key}
                                    className="greek-temple-card-image"
                                    preview={false}
                                />
                            ))}
                        </div>
                        <Paragraph type="secondary" className="greek-temple-centered">
                            {t("games.details.greekTemple.setup.footer")}
                        </Paragraph>
                    </CustomCard>
                ),
            },
            {
                title: t("games.details.greekTemple.steps.turns.title"),
                description: t("games.details.greekTemple.steps.turns.description"),
                content: (
                    <CustomCard>
                        <Paragraph>{t("games.details.greekTemple.turns.line1")}</Paragraph>
                        <Paragraph>
                            {t("games.details.greekTemple.turns.line2")}{" "}
                            <Text type="success">
                                <ArrowUpOutlined /> {t("games.details.greekTemple.turns.higher")}
                            </Text>{" "}
                            {t("games.details.greekTemple.turns.or")}{" "}
                            <Text type="danger">
                                <ArrowDownOutlined /> {t("games.details.greekTemple.turns.lower")}
                            </Text>
                        </Paragraph>
                        <Divider />
                        <Row gutter={[16, 16]} justify="center">
                            {sampleTurns.map((pillar) => (
                                <Col xs={24} md={8} key={pillar.id}>
                                    <Card size="small" title={<Text strong>{t("games.details.greekTemple.turns.pillar", { id: pillar.id })}</Text>} bordered>
                                        <Space align="center" direction="vertical" className="greek-temple-full-width">
                                            <div className="greek-temple-card-pair">
                                                <Image src={`/cards/${pillar.base}.png`} width={46} height={64} alt="Base card" preview={false} className="greek-temple-card-image" />
                                                <Image
                                                    src={`/cards/${pillar.newCard}.png`}
                                                    width={46}
                                                    height={64}
                                                    alt="New card"
                                                    preview={false}
                                                    className="greek-temple-card-image greek-temple-card-image--new"
                                                />
                                            </div>
                                            <Text type="secondary">{t("games.details.greekTemple.turns.prompt")}</Text>
                                        </Space>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                        <Paragraph className="greek-temple-turn-result">{t("games.details.greekTemple.turns.correct")}</Paragraph>
                        <Paragraph>{t("games.details.greekTemple.turns.wrong")}</Paragraph>
                    </CustomCard>
                ),
            },
            {
                title: t("games.details.greekTemple.steps.next.title"),
                description: t("games.details.greekTemple.steps.next.description"),
                content: (
                    <CustomCard>
                        <Paragraph>{t("games.details.greekTemple.next.line1")}</Paragraph>
                        <Paragraph>{t("games.details.greekTemple.next.line2")}</Paragraph>
                        <Divider />
                        <PillarStack items={stackedPillars} />
                        <Paragraph type="secondary" italic>
                            {t("games.details.greekTemple.next.collapse")}
                        </Paragraph>
                        <Divider />
                        <Space direction="vertical" size="middle" className="greek-temple-full-width">
                            <Text>{t("games.details.greekTemple.next.goal")}</Text>
                            <Text>{t("games.details.greekTemple.next.drink")}</Text>
                        </Space>
                    </CustomCard>
                ),
            },
            {
                title: t("games.details.greekTemple.steps.reset.title"),
                description: t("games.details.greekTemple.steps.reset.description"),
                content: (
                    <CustomCard>
                        <Paragraph>
                            {t("games.details.greekTemple.reset.line1")} {t("games.details.greekTemple.reset.line2")}
                        </Paragraph>
                        <PillarStack items={resetSamples} />
                        <Paragraph type="secondary" className="greek-temple-centered">
                            {t("games.details.greekTemple.reset.footer")}
                        </Paragraph>
                    </CustomCard>
                ),
            },
        ],
        [t]
    );

    return (
        <GamePage
            game="greekTemple"
            tagColor="gold"
            instructionIcons={[<AppstoreAddOutlined key="setup" />, <ArrowUpOutlined key="turns" />, <ArrowDownOutlined key="next" />, <ReloadOutlined key="reset" />]}
        >
                <Steps
                    className="greek-temple-steps"
                    direction="vertical"
                    current={current}
                    onChange={setCurrent}
                    items={buildStepItems(steps, current)}
                />
                        <div className="greek-temple-step-content">{steps[current]?.content}</div>
        </GamePage>
    );
};

export default GrichischerTempel;
